import {
  Accordion,
  Select,
  Stack,
  TagsInput,
  Textarea,
  TextInput,
  Badge,
  Group,
  Button,
  Modal,
  Text,
  Loader,
  Table,
  Center,
  Paper,
  Divider,
  Grid,
  ActionIcon
} from '@mantine/core';
import React, { useState, useEffect } from 'react';
import styles from './styles.module.css';
import { Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { useFormContext } from 'providers/FormProviders';

interface BasicInformationFormProps {
  control: any;
  errors: any;
}

import { useCourseStore } from 'store/createCourseStore';
import {
  trainingCatalogueService,
  trainingCatalogueDetailService,
  trainingCatalogueCompetencyDetailService
} from 'services/lcms/trainingCatalogue.service';
import useDebounce from 'hooks/useDebounce';
import { IconEye, IconX } from '@tabler/icons-react';

// Helper function to safely decode HTML entities and strip tags
const stripHtmlAndDecode = (html: string): string => {
  if (!html) return '';

  // Create a temporary DOM element to safely decode HTML entities
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;

  // Get text content (this automatically decodes entities and strips tags)
  return (tempElement.textContent || tempElement.innerText || '').replace(/\s+/g, ' ').trim();
};

const BasicInformationForm: React.FC<BasicInformationFormProps> = () => {
  const { methods } = useFormContext();
  const { formData, mode, setFormData } = useCourseStore();

  // Local state for TNA Course selector (only in add mode)
  const [tnaModalOpen, setTnaModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);
  const [tnaDetailsMap, setTnaDetailsMap] = useState<Record<string, any>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const debouncedSearch = useDebounce(search, 400);

  // Keep a map of selected TNA id -> detail data (to show courseName instead of id)
  useEffect(() => {
    let mounted = true;
    const ids: string[] = formData.tnaCourses || [];
    if (!ids || ids.length === 0) {
      setTnaDetailsMap({});
      return;
    }

    (async () => {
      try {
        const results = await Promise.all(
          ids.map(async (id) => {
            try {
              const res: any = await trainingCatalogueDetailService.detail(id);
              // service returns { data: { ... } }
              return res?.data || null;
            } catch {
              return null;
            }
          })
        );

        if (!mounted) return;

        const map: Record<string, any> = {};
        ids.forEach((id, idx) => {
          map[id] = results[idx] || null;
        });
        setTnaDetailsMap(map);
      } catch {
        if (!mounted) return;
        setTnaDetailsMap({});
      }
    })();

    return () => {
      mounted = false;
    };
  }, [formData.tnaCourses]);

  const { data: tnaList, isFetching: loadingTnaList } = useQuery({
    queryKey: ['trainingCatalogue-basicInfo', { search: debouncedSearch, page, pageSize }],
    queryFn: () =>
      trainingCatalogueService.list({
        search: debouncedSearch,
        pageNumber: page,
        pageSize
      }),
    enabled: tnaModalOpen && mode === 'add'
  });

  // Detail queries for selected training
  const { data: detailData, isFetching: loadingDetail } = useQuery({
    queryKey: ['trainingCatalogueDetail-basicInfo', selectedDetailId],
    queryFn: () => trainingCatalogueDetailService.detail(selectedDetailId!),
    enabled: !!selectedDetailId && detailModalOpen
  });

  const { data: competencyData, isFetching: loadingCompetency } = useQuery({
    queryKey: ['trainingCatalogueCompetencyDetail-basicInfo', selectedDetailId],
    queryFn: () => trainingCatalogueCompetencyDetailService.detail(selectedDetailId!),
    enabled: !!selectedDetailId && detailModalOpen
  });

  const canAddMore = (formData.tnaCourses?.length || 0) < 12;

  const addTnaCourse = (id: string) => {
    if (!canAddMore) return;
    if (formData.tnaCourses?.includes(id)) return;
    setFormData({ tnaCourses: [...(formData.tnaCourses || []), id] });
  };

  const handleViewDetail = (id: string) => {
    setSelectedDetailId(id);
    setDetailModalOpen(true);
  };

  const {
    control,
    formState: { errors }
  } = methods;

  const params = { pageNumber: 1, pageSize: 10000 };

  const { data: dataCourseCategory, isPending: loadingCourseCategory } = useQuery({
    queryKey: ['courseServiceGetCourseCategories'],
    queryFn: () => courseService.getCourseCategories(params)
  });

  const { data: tagData, isPending: loadingGetTag } = useQuery({
    queryKey: ['courseServiceGetLCMSTags'],
    queryFn: () => courseService.getLCMSTags(params)
  });

  const removeTnaCourse = (id: string) => {
    setFormData({ tnaCourses: (formData.tnaCourses || []).filter((x) => x !== id) });
  };

  return (
    <>
      <Accordion.Item className={styles.accordionItem} value='basicInformation'>
        <Accordion.Control className={styles.accordionControl}>Basic Information</Accordion.Control>
        <Accordion.Panel className={styles.accordionPanel}>
          <Stack gap='lg' py={16}>
            {mode === 'add' && (
              <Stack gap={6}>
                <Group justify='space-between'>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>TNA Courses</div>
                  <Button
                    size='xs'
                    variant='default'
                    onClick={() => setTnaModalOpen(true)}
                    disabled={!canAddMore}
                  >
                    {canAddMore ? 'Add TNA Course' : 'Max 12 reached'}
                  </Button>
                </Group>
                {formData.tnaCourses && formData.tnaCourses.length > 0 ? (
                  <Group gap={6} wrap='wrap'>
                    {formData.tnaCourses.map((id) => (
                      <Badge
                        key={id}
                        variant='light'
                        color='blue'
                        size='sm'
                        rightSection={
                          <ActionIcon
                            size='xs'
                            variant='subtle'
                            color='blue'
                            onClick={() => removeTnaCourse(id)}
                          >
                            <IconX size={12} />
                          </ActionIcon>
                        }
                      >
                        {tnaDetailsMap[id]?.courseName || id}
                      </Badge>
                    ))}
                  </Group>
                ) : (
                  <Text fz={12} c='secondary'>
                    No TNA courses selected yet.
                  </Text>
                )}
              </Stack>
            )}
            <Controller
              name='courseName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Course Name'
                  placeholder='Enter course name'
                  error={errors.courseName?.message as string}
                  {...field}
                  withAsterisk
                />
              )}
            />

            <Controller
              name='courseCategory'
              control={control}
              render={({ field }) => (
                <Select
                  label='Course Category'
                  placeholder={loadingCourseCategory ? 'Loading...' : 'Select course category'}
                  data={
                    dataCourseCategory?.data?.map((item: { id: number; name: string }) => ({
                      value: item.id.toString(),
                      label: item.name
                    })) || []
                  }
                  searchable
                  disabled={loadingCourseCategory}
                  error={errors.courseCategory?.message as string}
                  value={field.value}
                  onChange={(value) => field.onChange(value || '')}
                  withAsterisk
                />
              )}
            />

            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Course Summary (optional)'
                  placeholder='Enter course summary'
                  minRows={3}
                  error={errors.description?.message as string}
                  {...field}
                />
              )}
            />

            <Controller
              name='tags'
              control={control}
              render={({ field }) => (
                <TagsInput
                  label='Tags (optional)'
                  placeholder={loadingGetTag ? 'Loading...' : 'Enter tags (comma separated)'}
                  error={errors.tags?.message as string}
                  onChange={(values) => field.onChange(values.toString())}
                  value={field.value ? field.value.split(',').filter(Boolean) : []}
                  disabled={loadingGetTag}
                  data={
                    tagData?.data?.map(
                      (item: { id: number; name: string; rawName: string }) => item.rawName
                    ) || []
                  }
                />
              )}
            />

            <Controller
              name='status'
              control={control}
              render={({ field }) => (
                <Select
                  label='Status'
                  placeholder='Select status'
                  data={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'completed', label: 'Completed' }
                  ]}
                  error={errors.status?.message as string}
                  value={field.value || 'draft'}
                  onChange={(value) => field.onChange(value || 'draft')}
                  withAsterisk
                />
              )}
            />
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
      {mode === 'add' && (
        <Modal
          opened={tnaModalOpen}
          onClose={() => setTnaModalOpen(false)}
          title={
            <Stack gap={4}>
              <Text fz={18} fw={700} c='dark.8'>
                Add TNA Courses
              </Text>
              <Text fz={13} c='secondary.6'>
                Select training courses to include in this course (up to 12 total)
              </Text>
            </Stack>
          }
          size='xl'
          padding='lg'
        >
          <Stack gap='md'>
            <TextInput
              placeholder='Search training by course name, code, or program...'
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
              rightSection={loadingTnaList ? <Loader size='xs' /> : undefined}
              size='md'
              styles={{
                input: {
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  '&:focus': {
                    borderColor: '#228be6',
                    boxShadow: '0 0 0 2px rgba(34, 139, 230, 0.2)'
                  }
                }
              }}
            />

            <Group justify='space-between' align='center'>
              <Text fz={12} c='secondary.6'>
                {loadingTnaList
                  ? 'Searching...'
                  : `Found ${tnaList?.totalRecords || 0} training(s)`}
              </Text>
              <Badge size='sm' variant='light' color='blue'>
                Selected: {formData.tnaCourses?.length || 0}/12
              </Badge>
            </Group>

            <Paper withBorder radius='md' style={{ overflow: 'hidden' }}>
              <Table.ScrollContainer minWidth={700}>
                <Table striped highlightOnHover stickyHeader withRowBorders={false}>
                  <Table.Thead bg='gray.0'>
                    <Table.Tr>
                      <Table.Th style={{ padding: '12px 16px' }}>
                        <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                          Course Code
                        </Text>
                      </Table.Th>
                      <Table.Th style={{ padding: '12px 16px' }}>
                        <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                          Course Name
                        </Text>
                      </Table.Th>
                      <Table.Th style={{ padding: '12px 16px' }}>
                        <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                          Program
                        </Text>
                      </Table.Th>
                      <Table.Th style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Text fz={12} fw={700} c='dark.7' tt='uppercase'>
                          Actions
                        </Text>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {loadingTnaList && (
                      <Table.Tr>
                        <Table.Td colSpan={4} style={{ padding: '24px', textAlign: 'center' }}>
                          <Center>
                            <Stack align='center' gap={12}>
                              <Loader size='md' variant='dots' />
                              <Text fz={14} c='secondary.6'>
                                Loading training data...
                              </Text>
                            </Stack>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {!loadingTnaList && (tnaList?.data?.length || 0) === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={4} style={{ padding: '40px', textAlign: 'center' }}>
                          <Stack align='center' gap={8}>
                            <Text fz={16} fw={600} c='secondary.7'>
                              No training found
                            </Text>
                            <Text fz={13} c='secondary.5'>
                              Try adjusting your search criteria
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {!loadingTnaList &&
                      (tnaList?.data || []).map((r: any) => {
                        const selected = formData.tnaCourses?.includes(r.courseId);
                        const disabled = selected || !canAddMore;

                        return (
                          <Table.Tr
                            key={r.courseId}
                            style={{
                              opacity: disabled && !selected ? 0.6 : 1,
                              backgroundColor: selected ? '#f8f9fa' : 'transparent'
                            }}
                          >
                            <Table.Td style={{ padding: '12px 16px' }}>
                              <Badge variant='light' color='blue' size='sm'>
                                {r.courseCode || r.courseId}
                              </Badge>
                            </Table.Td>
                            <Table.Td style={{ padding: '12px 16px' }}>
                              <Text fz={14} fw={500} lineClamp={2} style={{ maxWidth: 300 }}>
                                {r.courseName}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ padding: '12px 16px' }}>
                              <Text fz={13} c='secondary.7'>
                                {r.courseFrom || '-'}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <Group gap={6} justify='center'>
                                <Button
                                  size='xs'
                                  variant='subtle'
                                  color='blue'
                                  onClick={() => handleViewDetail(r.trainingRecommendationID)}
                                  leftSection={<IconEye size={14} />}
                                >
                                  View
                                </Button>
                                {selected ? (
                                  <Badge size='sm' color='teal' variant='light'>
                                    ✓ Added
                                  </Badge>
                                ) : (
                                  <Button
                                    size='xs'
                                    variant='light'
                                    disabled={disabled}
                                    onClick={() => addTnaCourse(r.courseId)}
                                    style={{
                                      borderRadius: '6px',
                                      fontWeight: 600
                                    }}
                                  >
                                    Add
                                  </Button>
                                )}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>

            <Group justify='space-between' mt='sm'>
              <Group gap={6}>
                <Text fz={12} c='secondary.6'>
                  Page {page} of {tnaList?.totalPages || 1}
                </Text>
                <Text fz={12} c='secondary.4'>
                  •
                </Text>
                <Text fz={12} c='secondary.6'>
                  {(page - 1) * pageSize + 1}-
                  {Math.min(page * pageSize, tnaList?.totalRecords || 0)} of{' '}
                  {tnaList?.totalRecords || 0}
                </Text>
              </Group>
              <Group gap={6}>
                <Button
                  size='xs'
                  variant='default'
                  disabled={page <= 1 || loadingTnaList}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{ borderRadius: '6px' }}
                >
                  Previous
                </Button>
                <Button
                  size='xs'
                  variant='default'
                  disabled={loadingTnaList || page >= (tnaList?.totalPages || 1)}
                  onClick={() => setPage((p) => p + 1)}
                  style={{ borderRadius: '6px' }}
                >
                  Next
                </Button>
              </Group>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Detail Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedDetailId(null);
        }}
        title={
          <Stack gap={4}>
            <Text fz={18} fw={700} c='dark.8'>
              Training Course Details
            </Text>
            <Text fz={13} c='secondary.6'>
              Comprehensive information about the selected training course
            </Text>
          </Stack>
        }
        size='lg'
        padding='xl'
      >
        {(loadingDetail || loadingCompetency) && (
          <Center py={40}>
            <Stack align='center' gap={16}>
              <Loader size='lg' variant='dots' />
              <Stack align='center' gap={4}>
                <Text fz={16} fw={600} c='dark.6'>
                  Loading course details...
                </Text>
                <Text fz={12} c='secondary.6'>
                  Fetching detailed training information
                </Text>
              </Stack>
            </Stack>
          </Center>
        )}

        {!loadingDetail && !loadingCompetency && detailData?.data && (
          <Stack gap='lg'>
            {/* Header Information */}
            <Paper withBorder p='md' radius='md' bg='blue.0'>
              <Stack gap={12}>
                <Group justify='space-between' align='flex-start'>
                  <div style={{ flex: 1 }}>
                    <Text fz={16} fw={700} c='dark.8' mb={4}>
                      {detailData.data.courseName}
                    </Text>
                    <Group gap={8} wrap='wrap'>
                      <Badge variant='light' color='blue' size='sm'>
                        {detailData.data.moduleCode}
                      </Badge>
                      {detailData.data.trainingCategoryDesc && (
                        <Badge variant='outline' color='orange' size='sm'>
                          {detailData.data.trainingCategoryDesc}
                        </Badge>
                      )}
                      <Badge variant='filled' color='gray' size='sm'>
                        {detailData.data.pnaYear}
                      </Badge>
                    </Group>
                  </div>
                </Group>
              </Stack>
            </Paper>

            {/* Basic Information */}
            <Stack gap='md'>
              <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                Basic Information
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Program Name
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.programName || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Training Method
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.trainingMethodName || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Learning Hours
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.learningHours
                        ? `${detailData.data.learningHours} hours`
                        : '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Training Model
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.trainingModelDesc || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>

            <Divider />

            {/* Financial Information */}
            <Stack gap='md'>
              <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                Financial Information
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Budget
                    </Text>
                    <Text fz={13} c='dark.7' fw={600}>
                      {detailData.data.budget
                        ? `Rp ${detailData.data.budget.toLocaleString()}`
                        : '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Total Cost
                    </Text>
                    <Text fz={13} c='dark.7' fw={600}>
                      {detailData.data.totalCost
                        ? `Rp ${detailData.data.totalCost.toLocaleString()}`
                        : '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>

            <Divider />

            {/* Competency Information */}
            {competencyData?.data && (
              <>
                <Stack gap='md'>
                  <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                    Competency Details
                  </Text>
                  <Grid>
                    <Grid.Col span={6}>
                      <Stack gap={4}>
                        <Text fz={12} c='secondary.6' fw={600}>
                          Competency Category
                        </Text>
                        <Text fz={13} c='dark.7'>
                          {competencyData.data.competencyCategoryDesc || '-'}
                        </Text>
                      </Stack>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Stack gap={4}>
                        <Text fz={12} c='secondary.6' fw={600}>
                          Urgency Level
                        </Text>
                        <Badge
                          size='sm'
                          color={
                            competencyData.data.urgencyDesc?.includes('Urgent') ? 'red' : 'yellow'
                          }
                          variant='light'
                        >
                          {competencyData.data.urgencyDesc || '-'}
                        </Badge>
                      </Stack>
                    </Grid.Col>
                  </Grid>
                </Stack>
                <Divider />
              </>
            )}

            {/* Strategic Information */}
            <Stack gap='md'>
              <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                Strategic Information
              </Text>
              <Stack gap={12}>
                <div>
                  <Text fz={12} c='secondary.6' fw={600} mb={4}>
                    Strategic Objectives
                  </Text>
                  <Text fz={13} c='dark.7'>
                    {detailData.data.strategicObjectives || '-'}
                  </Text>
                </div>
                <div>
                  <Text fz={12} c='secondary.6' fw={600} mb={4}>
                    Performance Measures
                  </Text>
                  <Text fz={13} c='dark.7'>
                    {detailData.data.performanceMeasures || '-'}
                  </Text>
                </div>
              </Stack>
            </Stack>

            <Divider />

            {/* Learning Objectives */}
            <Stack gap='md'>
              <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                Learning Objectives
              </Text>
              <Paper withBorder p='md' radius='md' bg='gray.0'>
                <Text fz={13} c='dark.7'>
                  {detailData.data.learningObjectives
                    ? stripHtmlAndDecode(detailData.data.learningObjectives)
                    : 'No learning objectives specified'}
                </Text>
              </Paper>
            </Stack>

            {/* Additional Information */}
            <Stack gap='md'>
              <Text fz={14} fw={700} c='dark.7' tt='uppercase'>
                Additional Information
              </Text>
              <Grid>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Competency Name
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.competencyName || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Training Provider
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.trainingProvider || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Schedule
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.scheduleDesc || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Stack gap={4}>
                    <Text fz={12} c='secondary.6' fw={600}>
                      Impact Level
                    </Text>
                    <Text fz={13} c='dark.7'>
                      {detailData.data.levelImpactDesc || '-'}
                    </Text>
                  </Stack>
                </Grid.Col>
              </Grid>
            </Stack>
          </Stack>
        )}

        {!loadingDetail && !loadingCompetency && !detailData?.data && (
          <Center py={40}>
            <Stack align='center' gap={8}>
              <Text fz={16} fw={600} c='secondary.7'>
                No details available
              </Text>
              <Text fz={13} c='secondary.5'>
                Unable to load training course information
              </Text>
            </Stack>
          </Center>
        )}
      </Modal>
    </>
  );
};

export default BasicInformationForm;
